// stores/contact/useContactStore.ts

import { defineStore } from 'pinia'
import { createIndexedDBStore } from '@/stores/_base/createIndexedDBStore'
import { createContactService } from '@/services/contact/contactService'
import type { Contact } from '@farmfree/common/types/contact/contact'
import type { ApiResponse } from '@farmfree/common/types/apiResponse'
import { useAuthStore } from '@/stores/auth/useAuthStore'
import { computed } from 'vue'

// 연락처 데이터 래퍼 타입
interface ContactData {
  id: string
  contacts: Contact[]
  dateCreated?: Date
  dateModified?: Date
}

// createIndexedDBStore 팩토리를 사용하여 기본 Store 생성
const baseContactStore = createIndexedDBStore<ContactData>({
  storeId: 'contact',
  tableName: 'contacts',
  versionField: 'contactVersion',
  service: {
    getModified: async (companyId: string, since: number): Promise<ApiResponse<ContactData[]>> => {
      const contactService = createContactService()
      const authStore = useAuthStore()
      const adminUid = authStore.currentUser?.uid

      if (!adminUid) {
        return {
          isSuccess: false,
          message: '관리자 UID가 없습니다.',
          data: [],
        }
      }

      // 연락처 로드
      const result = await contactService.getContacts(adminUid)

      if (!result.isSuccess || !result.data) {
        return {
          isSuccess: false,
          message: result.message || '연락처 로드 실패',
          data: [],
        }
      }

      // ContactData 형태로 반환
      const contactData: ContactData = {
        id: companyId,
        contacts: result.data,
        dateCreated: new Date(),
        dateModified: new Date(),
      }

      return {
        isSuccess: true,
        data: [contactData],
      }
    },

    getDeleted: async (companyId: string): Promise<ApiResponse<string[]>> => {
      // 연락처는 삭제되지 않음
      return {
        isSuccess: true,
        data: [],
      }
    },
  },
  options: {
    debug: false,
  },
})

// 확장된 contactStore 생성
export const useContactStore = defineStore('contactExtended', () => {
  const store = baseContactStore()

  // 연락처 관련 computed
  const contacts = computed(() => {
    const contactData = store.items[0]
    return contactData?.contacts || []
  })

  // 전화번호를 키로 하는 연락처 맵 (빠른 검색용)
  const contactsMap = computed(() => {
    const map = new Map<string, Contact>()

    contacts.value.forEach((contact) => {
      // 전화번호를 여러 형태로 저장하여 매칭률 높이기
      const phone = contact.phone
      if (phone) {
        // 원본 전화번호
        map.set(phone, contact)

        // 하이픈 제거
        const phoneWithoutHyphen = phone.replace(/-/g, '')
        map.set(phoneWithoutHyphen, contact)

        // 국가번호 제거 (+82 10 -> 010)
        if (phoneWithoutHyphen.startsWith('8210')) {
          const localPhone = '0' + phoneWithoutHyphen.substring(2)
          map.set(localPhone, contact)
        }

        // 앞자리 0 제거 (010 -> 10)
        if (phoneWithoutHyphen.startsWith('0')) {
          const phoneWithout0 = phoneWithoutHyphen.substring(1)
          map.set(phoneWithout0, contact)
        }
      }
    })

    return map
  })

  // 통계
  const stats = computed(() => ({
    totalContacts: contacts.value.length,
    withPhone: contacts.value.filter((c) => c.phone).length,
    withEmail: contacts.value.filter((c) => c.email).length,
    withGroup: contacts.value.filter((c) => c.groups && c.groups.length > 0).length,
  }))

  // 로드 상태
  const isLoaded = computed(() => contacts.value.length > 0)

  // 메서드
  const getContactByPhone = (phoneNumber: string | undefined): Contact | undefined => {
    if (!phoneNumber) return undefined

    // 여러 형태로 검색
    const normalizedPhone = phoneNumber.replace(/-/g, '')

    return (
      contactsMap.value.get(phoneNumber) ||
      contactsMap.value.get(normalizedPhone) ||
      contactsMap.value.get(normalizedPhone.replace(/^8210/, '0')) ||
      contactsMap.value.get(normalizedPhone.replace(/^0/, ''))
    )
  }

  const searchContacts = (query: string): Contact[] => {
    const lowerQuery = query.toLowerCase()
    return contacts.value.filter((contact) => {
      const nameMatch = contact.name?.toLowerCase().includes(lowerQuery)
      const phoneMatch = contact.phone?.includes(query)
      const emailMatch = contact.email?.toLowerCase().includes(lowerQuery)
      return nameMatch || phoneMatch || emailMatch
    })
  }

  // 초기화 메서드 - handleCompanyChange에서 호출
  const loadContacts = async () => {
    const authStore = useAuthStore()
    const companyId = authStore.company?.id
    if (!companyId) return

    // syncWithServer를 통해 데이터 로드
    await store.syncWithServer(companyId)
  }

  // 레거시 호환 메서드
  const reset = () => {
    store.$reset()
  }

  return {
    ...store,
    // computed
    contacts,
    contactsMap,
    stats,
    isLoaded,
    // 메서드
    getContactByPhone,
    searchContacts,
    loadContacts,
    reset,
  }
})
